import { useState, useCallback, useRef, useEffect } from "react";
import { useUser } from "@webuild-ai/react-auth";

import { useChatStore } from "@/stores/ChatStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { REGENERATION_STRATEGIES, MAX_REGENERATION_ATTEMPTS } from "@/constants/regeneration";
import { logger } from "@/telemetry";

interface UseRegenerationReturn {
  regenerate: () => Promise<void>;
  switchAttempt: (attemptNumber: number) => Promise<void>;
  loading: boolean;
  error: string | null;
  currentAttempt: number;
  attemptCount: number;
  shouldShowAttemptNavigation: boolean;
  hasReachedMaxAttempts: boolean;
}

// Helper to get variation strategy
const getVariationStrategy = (attemptNumber: number): string => {
  return REGENERATION_STRATEGIES[(attemptNumber - 1) % REGENERATION_STRATEGIES.length];
};

export const useRegeneration = (runId: string, messageId: string): UseRegenerationReturn => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Get necessary hooks
  const { chat, updateMessage, save } = useChatStore();
  const { platformClient, adminClient, sseWorkflowClient } = useWebuildAPIClient();
  const { userProfile } = useUser();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Get current message safely
  const currentMessage = chat?.messages?.find((m) => m.id === messageId);

  // Clear loading state when streaming completes
  useEffect(() => {
    if (!currentMessage) return;

    const messageStatus = currentMessage.status;

    if (loading && messageStatus && messageStatus !== "streaming") {
      setLoading(false);
      if (messageStatus === "success" && error) {
        setError(null);
      }
    }
  }, [currentMessage?.status, loading, error, messageId]);

  // Safe field access with defaults
  const attemptCount = currentMessage?.attempts || 1;
  const currentAttempt = currentMessage?.currentAttempt || 1;
  const shouldShowAttemptNavigation = attemptCount > 1;
  const hasReachedMaxAttempts = attemptCount >= MAX_REGENERATION_ATTEMPTS;

  // Helper to ensure SSE connection is active
  const ensureSSEConnection = useCallback(async () => {
    const { chat, runId: currentRunId } = useChatStore.getState();

    // Check if we have an active SSE connection for this runId
    const activeConnections = (window as any).__activeSSEConnections || {};
    const existingConnection = activeConnections[runId];
    const hasActiveConnection =
      existingConnection &&
      typeof existingConnection.isActive === "function" &&
      existingConnection.isActive() &&
      existingConnection.signal &&
      !existingConnection.signal.aborted;

    // Cancel any pending cleanup timeout if connection exists (for regeneration)
    if (existingConnection && typeof existingConnection.clearCleanupTimeout === "function") {
      existingConnection.clearCleanupTimeout();
    }

    const needsConnection = !chat || chat.id !== currentRunId || !hasActiveConnection;

    if (needsConnection && runId) {
      const { subscribe } = useChatStore.getState();
      const userId = userProfile?.id;

      if (subscribe && sseWorkflowClient && adminClient && userId) {
        await subscribe(sseWorkflowClient, adminClient, userId, undefined);
      } else {
        throw new Error("Cannot establish SSE connection");
      }
    }
  }, [sseWorkflowClient, adminClient, userProfile, runId]);

  const regenerate = useCallback(async () => {
    if (!platformClient || !runId || !messageId || !currentMessage) {
      logger.error("Missing required data for regeneration");
      setError("Cannot regenerate - missing required data");

      return;
    }

    try {
      // Set loading state
      setLoading(true);
      setError(null);

      // Create abort controller for this request
      abortControllerRef.current = new AbortController();

      // Ensure SSE connection is active
      await ensureSSEConnection();

      // Determine variation strategy and call API
      const variationStrategy = getVariationStrategy(currentAttempt + 1);
      const response = await platformClient.workflows.regenerateMessage({
        workflowId: runId,
        messageId: messageId,
        variationStrategy: variationStrategy,
      });

      // Update message metadata and pre-populate with first chunk if available
      if (chat?.id) {
        const newAttempts = Math.max((attemptCount || 1) + 1, response?.total_attempts || 1);
        const newCurrentAttempt = response?.attempt_number || newAttempts;

        // Prepare the message update
        const messageUpdate: any = {
          attempts: newAttempts,
          currentAttempt: newCurrentAttempt,
          status: "streaming", // Mark as streaming for SSE
        };

        // Pre-populate with first chunk if available from API response
        if (response?.content && response.content.trim()) {
          messageUpdate.content = response.content;
        }

        updateMessage(chat.id, messageId, messageUpdate);
      }
    } catch (err: any) {
      logger.error("❌ Regeneration failed", err);

      if (isMountedRef.current) {
        setError(err.message || "Failed to regenerate message");
        setLoading(false);
      }
    }
  }, [
    platformClient,
    runId,
    messageId,
    currentMessage,
    currentAttempt,
    attemptCount,
    chat,
    updateMessage,
    ensureSSEConnection,
  ]);

  // Switch to a different attempt
  const switchAttempt = useCallback(
    async (attemptNumber: number) => {
      if (!platformClient || !runId || !currentMessage || !isMountedRef.current) {
        logger.error("Cannot switch attempt - missing requirements or unmounted");
        setError("Missing platform client, runId, or message not found");

        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Call the real Platform API update message endpoint
        const updatedMessage = await platformClient.workflows.updateMessage({
          workflowId: runId,
          messageId: messageId,
          updateMessageRequest: {
            selectedAttempt: attemptNumber,
          },
        });

        if (!isMountedRef.current) return;

        if (chat?.id && updatedMessage) {
          // Extract actual content - use the content from the API response
          const actualContent = updatedMessage.content || "";

          const messageUpdates: any = {
            content: actualContent,
            status: "success",
            timestamp: new Date().toISOString(),
            attempts: updatedMessage.attempts,
            currentAttempt: updatedMessage.currentAttempt,
            attemptHistory:
              updatedMessage.attemptHistory || (updatedMessage as any).attempt_history,
          };

          updateMessage(chat.id, messageId, messageUpdates);

          // Persist to backend for page reload persistence
          if (adminClient) {
            try {
              const updatedChatHistory = {
                ...chat,
                messages: chat.messages.map((msg) =>
                  msg.id === messageId ? { ...msg, ...messageUpdates } : msg,
                ),
              };

              await save(adminClient, updatedChatHistory);
            } catch (error) {
              logger.error("❌ Failed to persist attempt selection", error);
            }
          }
        }
      } catch (err) {
        if (isMountedRef.current) {
          logger.error("❌ Switch attempt failed", err);
          setError((err as any)?.message || "Failed to switch attempt");
        }
      } finally {
        setLoading(false);
      }
    },
    [
      platformClient,
      adminClient,
      runId,
      messageId,
      currentMessage,
      chat,
      updateMessage,
      save,
      isMountedRef,
    ],
  );

  return {
    regenerate,
    switchAttempt,
    loading,
    error,
    currentAttempt,
    attemptCount,
    shouldShowAttemptNavigation,
    hasReachedMaxAttempts,
  };
};
